import { Component, OnInit } from '@angular/core';
import { UserService } from '@shared/services/user/user.service';
import { HttpClient } from "@angular/common/http";
import { Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil, tap } from "rxjs";
import { PlannerProjectService } from "@modules/planner/services/planner-project/planner-project.service";
import { WorkOrderService } from "@modules/planner/services/workorder/work-order.service";
import { WorkOrder } from '@shared/models/work-order';
import { Inverter, RowNumber } from '@modules/planner/components/weekly-planning-map/weekly-planning-map.component';
import { stat } from 'fs';
import { MapUtils } from "@shared/services/mapUtils/map-utils.service";
import { RoutePlanningService } from "@modules/planner/services/route-planning/route-planning.service";
import { BaseReport, RouteNote, RoutePlanningBase, RoutePlanningRoute } from "@shared/models/route-planning";
import { TranslateService } from "@ngx-translate/core";
import { ToastService } from "@shared/services/toast/toast.service";
import { CustomerIds } from "@shared/models/customer-settings";
import { DateUtils } from "@shared/services/dateUtils/date-utils.service";
import { BasemapService } from '@shared/services/basemap/basemap.service';

declare const L: any;

enum MapLayerFilter {
    ALL = 'all',
    FRAMES = 'frames',
    INVERTERS = 'inverters',
}

@Component({
    selector: 'app-overview-dashboard',
    templateUrl: './overview-dashboard.component.html',
    styleUrls: ['./overview-dashboard.component.scss'],
    providers: [HttpClient],
    standalone: false
})
export class OverviewDashboardComponent implements OnInit {

    selectedState: any
    selectedImage: any

    componentDestroyed$: Subject<boolean> = new Subject()
    allCalendars;
    teams = Array()
    projectId: any
    pageLoading = true

    monDate: any
    tueDate: any
    wedDate: any
    thuDate: any
    friDate: any
    satDate: any
    sunDate: any
    todaysDate: any
    chosenDate: any
    today: any

    calendarsLoading = true
    weekdays: any
    calendarObjectIsSet = false
    thisDayHover = -1
    hoveredCalendar = -1
    customCalendarObject = {}
    showCalendarsWithSlots = false

    msas: any
    userid: any
    projectData: any
    projectName: any
    msaEstimatesLoading = true

    //map related stuff
    workOrders: WorkOrder[] = [];
    map: any
    showColors = false
    interruptedColor = "#ff1122";

    customerId = 0;
    inverters: WorkOrder[] = []
    inverterMarkers: Map<number, L.Marker> = new Map();
    canvas = L.canvas()
    allStates: any = { "states": [] }
    showSidebar: boolean = false
    selectedMarker: any = { 'identifier': null, 'states': [], 'imagecount': 0 }
    type: number = 0
    workOrderMarkers: Map<number, L.Marker> = new Map();
    type3Workorders: WorkOrder[] = []
    type3WorkOrderMarkers: Map<number, L.Marker> = new Map();
    powerLineWorkOrders: WorkOrder[] = []
    powerLineWorkOrderMarkers: Map<number, L.Marker> = new Map();

    rowNumberMarkers;
    rowNumbers;

    layerFilter: MapLayerFilter = MapLayerFilter.ALL;
    hasInverters = false;

    // Route planning
    customerIds = CustomerIds;
    baseMarkers: Map<number, L.Marker> = new Map();
    selectedBase!: RoutePlanningBase;
    selectedBaseReports: BaseReport[] = [];
    showBaseSidebar = false;
    selectedRoute!: RoutePlanningRoute;
    showRouteSidebar = false;
    routeNoteMarkers: Map<number, L.Marker> = new Map();
    selectedRouteNote!: RouteNote;
    showRouteNoteSidebar = false;
    routeTooltip = L.tooltip({ permanent: false, direction: 'bottom', offset: L.point(0, 10) });

    constructor(
        private userService: UserService,
        private plannerProjectService: PlannerProjectService,
        private workOrderService: WorkOrderService,
        private routePlanningService: RoutePlanningService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private basemapService: BasemapService,
    ) { }

    ngOnInit() {
        this.setUpCustomRectangleCanvas();
        this.setUpCustomTextMarkerCanvas();
        this.todaysDate = new Date();
        this.today = this.todaysDate.getDay();

        // Get the current project ID first
        this.getCurrentProjectId(() => {
            // Use switchMap to first get row numbers, then get project data
            this.workOrderService.getRowNumbers(this.projectId)
                .pipe(
                    switchMap(rowNumbers => {
                        this.rowNumbers = rowNumbers; // Store the row numbers

                        // After getting row numbers, fetch project data
                        return this.plannerProjectService.getProjectById(this.projectId);
                    }),
                    takeUntil(this.componentDestroyed$), // Ensure this happens after fetching project by ID
                    switchMap(projectData => {
                        this.projectData = projectData;
                        this.projectName = projectData.name;

                        // Fetch project statuses in parallel
                        return forkJoin({
                            status1: this.plannerProjectService.getProjectStatus(this.projectId, 1),
                            status2: this.plannerProjectService.getProjectStatus(this.projectId, 2),
                            status3: this.plannerProjectService.getProjectStatus(this.projectId, 3),
                            status4: this.plannerProjectService.getProjectStatus(this.projectId, 4),
                            status5: this.plannerProjectService.getProjectStatus(this.projectId, 5),
                        });
                    })
                )
                .subscribe({
                    next: statusData => {
                        // Combine status data into a single object
                        this.allStates = {
                            ...statusData.status1,
                            states: [
                                ...statusData.status1.states,
                                ...statusData.status2.states,
                                ...statusData.status3.states,
                                ...statusData.status4.states,
                                ...statusData.status5.states,
                            ].map(state => ({ ...state, show: false }))
                        };

                        this.pageLoading = false;

                        // Fetch work orders and inverters simultaneously
                        forkJoin({
                            workorders: this.getWorkOrders(null, 1),
                            inverters: this.getWorkOrders(null, 2),
                            type3Workorders: this.getWorkOrders(null, 3),
                            type4Workorders: this.getWorkOrders(null, 4),
                            type5Workorders: this.getWorkOrders(null, 5),
                        }).subscribe({
                            next: results => {
                                this.workOrders = results.workorders;
                                this.inverters = results.inverters;
                                this.hasInverters = Array.isArray(this.inverters) && this.inverters.length > 0;
                                this.type3Workorders = results.type3Workorders;
                                this.powerLineWorkOrders = results.type4Workorders.concat(results.type5Workorders);
                                this.initMap();
                            },
                            error: error => {
                                console.error('Error fetching inverters or work orders:', error);
                            }
                        });
                    },
                    error: error => {
                        console.error('Error fetching project or status:', error);
                    }
                });
        });
    }


    setUpCustomRectangleCanvas() {
        L.Canvas.include({
            _updateCustomRectangle: function (layer) {
                const ctx = this._ctx;
                const p = layer._point;
                const size = layer.options.size;
                const halfWidth = size[0] / 2;
                const halfHeight = size[1] / 2;

                ctx.save();
                ctx.beginPath();
                ctx.rect(p.x - halfWidth, p.y - halfHeight, size[0], size[1]);
                ctx.fillStyle = layer.options.fillColor;
                ctx.fill();
                ctx.strokeStyle = layer.options.color;
                ctx.lineWidth = layer.options.weight;
                ctx.stroke();
                ctx.restore();
            }
        });

        L.CustomRectangleMarker = L.CircleMarker.extend({
            options: {
                size: [20, 10], // width, height
            },

            _updatePath: function () {
                this._renderer._updateCustomRectangle(this);
            }
        });

        L.customRectangleMarker = function (latlng, options) {
            return new L.CustomRectangleMarker(latlng, options);
        };
    }

    getStatusDefinition() {
        const state = this.allStates.states.find(element => element.id == this.selectedMarker.status_id);
        if (state) {
            return state.definition;
        }
        return null;
    }

    setUpCustomTextMarkerCanvas() {
        L.Canvas.include({
            _updateCustomText: function (layer) {
                const ctx = this._ctx;
                const p = layer._point;
                const text = layer.options.text;
                const fontSize = layer.options.fontSize || 12;
                const fontColor = layer.options.fontColor || 'black';

                ctx.save();
                ctx.font = `${fontSize}px Arial`;
                ctx.fillStyle = fontColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(text, p.x, p.y);
                ctx.restore();
            }
        });

        L.CustomTextMarker = L.CircleMarker.extend({
            options: {
                text: '',
                fontSize: 12,
                fontColor: 'black'
            },

            _updatePath: function () {
                this._renderer._updateCustomText(this);
            }
        });

        L.customTextMarker = function (latlng, options) {
            return new L.CustomTextMarker(latlng, options);
        };

    }

    getInverters(projectId): Observable<Inverter[]> {
        return this.workOrderService.getInverters(projectId)
    }

    closeSidebar(): void {
        this.showSidebar = false;
        this.showBaseSidebar = false;
        this.showRouteSidebar = false;
        this.showRouteNoteSidebar = false;
        this.selectedState = null;
    }

    getWorkOrders(limit: number | null, type: number): Observable<WorkOrder[]> {
        return this.workOrderService.getWorkOrders(limit, type);
    }

    /**
     * Gets current project id and calls a callback method to tell that it has finished
     * @param cb
     */
    getCurrentProjectId(cb) {
        this.userService.getUserInfo().subscribe(
            data => {
                this.projectId = data.current_project
                this.userid = data.id
                this.customerId = data.customer_id;

                // cb() = callback to let the other function know that this one is finished
                cb()
            }
        );
    }

    initMap() {
        const centerCoordinates = this.getCenterCoordinates();

        if (this.map) {
            this.applyLayerFilter(); // redraw according to current filter
            return;
        }

        this.map = L.map('mapProject', { editable: true });

        this.map.zoomControl.setPosition('topright');

        this.basemapService.attach(this.map, { customerId: this.customerId, minZoom: 10, maxZoom: 20 });

        this.map.on('locationerror', () => {
            this.map.panTo({ lat: centerCoordinates.latitude, lon: centerCoordinates.longitude });
        });

        // Set the initial center of the map
        // Set the initial center of the map
        const initialCenter = L.latLng(centerCoordinates.latitude, centerCoordinates.longitude);
        let mapZoom = 15
        if (this.customerId === 1) mapZoom = 16
        else if (this.customerId === 4) mapZoom = 13
        this.map.setView(initialCenter, mapZoom);

        this.drawRowNumbersOnMap();
        this.applyLayerFilter();  // ONE call; draws per filter (ALL by default)
        this.addZoomListener();

        if (this.customerId === this.customerIds.POWERLINES) {
            this.drawBases(this.projectId);
            this.drawRoutes(this.projectId);
            this.drawRouteNotes(this.projectId);
        }
    }

    drawPowerLineWorkOrders(): void {
        for (let workOrder of this.powerLineWorkOrders) {
            this.createPowerLineMarker(workOrder);
        }
    }

    private createPowerLineMarker(workOrder: WorkOrder): void {
        let color = '#000';

        // interrupted
        if (workOrder.interrupted) {
            color = workOrder.color;
        }

        const marker = MapUtils.getPowerlineCustomMarker(workOrder, color, 30, this.map);

        marker.on('click', () => this.showDetails(workOrder.id, true));
        marker.bindTooltip(`
          <div style="text-align: center;">
              <div>${workOrder.identifier}</div>
          </div>`, {
            direction: 'top',
            offset: [0, -10]
        });

        this.powerLineWorkOrderMarkers.set(workOrder.id, marker);
    }

    drawRowNumbersOnMap(): void {
        // Remove any existing row number markers from the map
        if (this.rowNumberMarkers) {
            this.rowNumberMarkers.forEach(marker => marker.remove());
        }
        this.rowNumberMarkers = new Map<number, L.Layer>(); // Reset the row number markers array

        const canvasRenderer = L.canvas(); // Create a canvas renderer

        this.rowNumbers.forEach((rowNumber) => {
            const lat = Number(rowNumber.position_x);
            const lng = Number(rowNumber.position_y);

            if (!isNaN(lat) && !isNaN(lng)) {
                // Create a custom text marker
                const marker = L.customTextMarker([lat, lng], {
                    renderer: canvasRenderer,
                    text: rowNumber.number.toString(),
                    fontSize: 11, // Adjust font size as needed
                    fontColor: 'black' // Adjust font color as needed
                }).addTo(this.map);

                // Add the marker to the rowNumberMarkers array
                this.rowNumberMarkers.set(rowNumber.id, marker);
            } else {
                console.error(`Invalid coordinates for row number id=${rowNumber.id}: lat=${lat}, lng=${lng}`);
            }
        });
    }



    drawType3Workorders() {
        // Remove any existing work order markers from the map
        if (this.type3WorkOrderMarkers) {
            this.type3WorkOrderMarkers.forEach(marker => marker.remove());
        }
        this.type3WorkOrderMarkers = new Map(); // Reset the work order markers array


        if (!this.map.getPane('workorderPane2')) {
            this.map.createPane('workorderPane2')
            this.map.getPane('workorderPane2').style.zIndex = 10
        }

        const zoom = this.map.getZoom()


        this.type3Workorders.forEach((workorder: WorkOrder) => {
            let color = 'green';
            let border = 'green';
            if (typeof workorder.status_id === 'number') {

                if (workorder.interrupted) {
                    color = this.interruptedColor;
                    border = this.interruptedColor;
                } else {
                    for (let i = 0; i < this.allStates.states.length; i++) {
                        const element = this.allStates.states[i];
                        if (element.id == workorder.status_id) {
                            color = element.color;
                            border = element.color;
                            break;
                        }
                    }
                }

                if (workorder.deviation) {
                    border = 'red';
                }

                if (workorder.comment !== 0) border = 'black';

                const opacity = 1;
                const lat = Number(workorder.latitude);
                const lng = Number(workorder.longitude);

                const size = MapUtils.getRelativeMarkerSize(workorder.frame_size, zoom, this.customerId)

                if (!isNaN(lat) && !isNaN(lng)) {
                    const marker = L.customRectangleMarker([lat, lng], {
                        pane: 'workorderPane2',
                        renderer: this.canvas,
                        size: size, // Size for rectangle
                        fillColor: color,
                        color: border,
                        opacity: opacity,
                        fillOpacity: opacity,
                        weight: 1, // Ensure the border weight is specified
                        isSelected: false,
                        frame_size: workorder.frame_size
                    }).addTo(this.map);

                    // Click event: show details
                    marker.on('click', () => this.showDetails(workorder.id, true));

                    // Add the marker to the workOrderMarkers map
                    this.type3WorkOrderMarkers.set(workorder.id, marker);
                } else {
                    console.error(`Invalid coordinates for workorder id=${workorder.id}: lat=${lat}, lng=${lng}`);
                }
            } else {
                console.error("Error, the workorder does not have a state")
            }
        });



    }

    drawInverters() {
        if (this.inverterMarkers) this.inverterMarkers.forEach(m => m.remove());
        this.inverterMarkers = new Map();

        if (!this.map.getPane('inverterPane')) {
            this.map.createPane('inverterPane');
            this.map.getPane('inverterPane').style.zIndex = 20;
        }

        const currentZoomLevel = this.map.getZoom();

        this.inverters.forEach((inverter) => {
            if (!inverter.frame_size) inverter.frame_size = 25;
            const size = MapUtils.getRelativeMarkerSize(inverter.frame_size, currentZoomLevel, this.customerId);

            const lat = Number(inverter.latitude);
            const lng = Number(inverter.longitude);
            if (isNaN(lat) || isNaN(lng)) {
                console.error(`Invalid coordinates for inverter id=${inverter.id}: lat=${lat}, lng=${lng}`);
                return;
            }

            // color/border like before
            const color = inverter.color;
            const border = inverter.deviation ? 'red' : inverter.color;

            // same radius logic as the other component (+ scale for customerId === 2)
            const scale = (this.customerId === 2) ? 2 : 1;
            const radius = (Math.max(size[0], size[1]) / 5) * scale;

            const marker = L.circleMarker([lat, lng], {
                pane: 'inverterPane',
                renderer: this.canvas,
                radius,
                fillColor: color,
                color: border,
                opacity: 1,
                fillOpacity: 1,
                weight: 1,
                isSelected: false,
                frame_size: inverter.frame_size
            } as any).addTo(this.map);

            (marker as any).options['size'] = size; // keep parity with other code
            marker.on('click', () => this.showDetails(inverter.id, false));
            this.inverterMarkers.set(inverter.id, marker);
        });
    }


    getStatusReports(stateId, selectedMarker) {
        const reports: any[] = []
        if (selectedMarker.reports) {
            for (let i = 0; i < selectedMarker.reports.length; i++) {
                const element = selectedMarker.reports[i]
                if (element.state == stateId) {
                    reports.push(element)
                }
            }
            return reports
        } else return []

    }

    showDetails(workorderOrInventerId: number, workorder: boolean): void {
        if (workorder) {
            this.allStates.states.forEach(state => state.show = false)
            this.workOrderService.getWorkOrder(workorderOrInventerId)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    data => {
                        this.showSidebar = true
                        this.selectedMarker = data
                        this.selectedMarker.imagecount = 0
                        this.type = data.type
                        Object.entries(data.statuses).forEach(([statusKey, status]: [string, any]) => {
                            status.count = status.images.length;
                            this.selectedMarker.imagecount += status.images.length;
                            if (status.images && status.images.length > 0) {
                                forkJoin(
                                    status.images.map((image: any) =>
                                        this.workOrderService.fetchImage(image.id).pipe(
                                            map(imageUrl => {
                                                image.url = imageUrl || 'defaultImageUrl'; // Update image URL or set default
                                                return image;
                                            }),
                                            catchError(error => {
                                                console.error('Error fetching image', error);
                                                return of(null);
                                            })
                                        )
                                    )
                                ).subscribe(images => {
                                    // All images for this status have been fetched and updated
                                });
                            }
                        });
                    }
                )
        } else {
            this.selectedMarker = this.inverters.find(i => i.id === workorderOrInventerId)
            this.showSidebar = true
            this.type = 2
        }
    }

    checkLength(array, type) {
        return array.filter(element => element.type === type).length
    }

    drawWorkOrders() {
        // Remove any existing work order markers from the map
        if (this.workOrderMarkers) {
            this.workOrderMarkers.forEach(marker => marker.remove());
        }
        this.workOrderMarkers = new Map(); // Reset the work order markers array


        if (!this.map.getPane('workorderPane')) {
            this.map.createPane('workorderPane')
            this.map.getPane('workorderPane').style.zIndex = 10
        }

        const currentZoomLevel = this.map.getZoom()

        this.workOrders.forEach((workorder: WorkOrder) => {
            let size = MapUtils.getRelativeMarkerSize(workorder.frame_size, currentZoomLevel, this.customerId)
            let color = 'gray';
            let border = 'gray';
            if (typeof workorder.status_id === 'number') {
                if (workorder.interrupted) {
                    color = this.interruptedColor;
                    border = this.interruptedColor;
                } else {
                    for (let i = 0; i < this.allStates.states.length; i++) {
                        const element = this.allStates.states[i];
                        if (element.id == workorder.status_id) {
                            color = element.color;
                            border = element.color;
                            break;
                        }
                    }
                }

                if (workorder.deviation) {
                    border = 'red';
                }

                const opacity = 1;
                const lat = Number(workorder.latitude);
                const lng = Number(workorder.longitude);

                if (!isNaN(lat) && !isNaN(lng)) {
                    const marker = L.customRectangleMarker([lat, lng], {
                        pane: 'workorderPane',
                        renderer: this.canvas,
                        size: size, // Size for rectangle
                        fillColor: color,
                        color: border,
                        opacity: opacity,
                        fillOpacity: opacity,
                        weight: 1, // Ensure the border weight is specified
                        isSelected: false // Initialize as not selected
                        , frame_size: workorder.frame_size
                    }).addTo(this.map);

                    // Click event: show details
                    marker.on('click', () => this.showDetails(workorder.id, true));

                    // Add the marker to the workOrderMarkers map
                    this.workOrderMarkers.set(workorder.id, marker);
                } else {
                    console.error(`Invalid coordinates for workorder id=${workorder.id}: lat=${lat}, lng=${lng}`);
                }
            } else {
                console.error("Error, the workorder does not have a state")
            }
        });



    }

    createIcon(size: number, color, opacity, border) {
        return L.divIcon({
            className: '',
            iconSize: [size, size], // square size
            html: `<div style="width: 85%; height: 70%; background-color: ${color}; opacity: ${opacity}; border: ${border};"></div>`
        });
    }

    addZoomListener() {
        this.map.on('zoomend', () => {
            const currentZoomLevel = this.map.getZoom();
            const weight = MapUtils.getBorderWeight(currentZoomLevel, 18, 0.3);

            this.workOrderMarkers.forEach((marker: any) => {
                marker.options['size'] = MapUtils.getRelativeMarkerSize(marker.options.frame_size, currentZoomLevel, this.customerId)
                marker.setStyle({ weight: weight });
            });

            this.inverterMarkers.forEach((marker: any) => {
                const newSize = MapUtils.getRelativeMarkerSize(marker.options.frame_size, currentZoomLevel, this.customerId);
                marker.options['size'] = newSize;
                const scale = (this.customerId === 2) ? 2 : 1;
                const newRadius = (Math.max(newSize[0], newSize[1]) / 5) * scale;
                marker.setRadius(newRadius);
                marker.setStyle({ weight: weight });
            });

            this.type3WorkOrderMarkers.forEach((marker: any) => {
                marker.options['size'] = MapUtils.getRelativeMarkerSize(marker.options.frame_size, currentZoomLevel, this.customerId)
                marker.setStyle({ weight: weight });
            });
        });
    }

    calculateIconSizeBasedOnZoom(zoomLevel: number): number {
        // return 8 * (zoomLevel / 10); // Example calculation

        switch (zoomLevel) {
            case 16:
                return 8
            case 17:
                return 16
            case 18:
                return 40
            case 19:
                return 80
            default:
                return 8
        }
    }


    /**
     * Converts a 2022-05-01 datestring to 01.05.2022 datestring.
     * @param datestring
     * @returns
     */
    dashesToDots(datestring) {
        var newdate = datestring.split("-").reverse().join("-")

        newdate = newdate.replace(/\-/g, '.')

        return newdate
    }

    // todo aja vain kerran
    // tällä hetkellä suoritetaan aina kun esim. hiiri liikkuu näytöllä
    filterNotStartedState(states: any[]): any[] {
        return states.filter(s =>
            !(
                s.definition === 'Not started' ||
                s.definition === 'Done' ||
                s.definition === 'Ei aloitettu' ||
                s.definition === 'Valmis'
            )
        );
    }


    setProgress(amount: number, total: number, givenColor: any = null) {
        let progress;
        if (total > 0) {
            progress = parseFloat((amount / total * 100).toFixed(2))
        } else {
            progress = 0
        }

        let color = "#0058ff"
        // let color = "#FF9F0A"
        // if(progress >= 90 && progress <= 100) {
        //     color = "#10A231"
        // } else if(progress > 100) {
        //     color = "#0058FF"
        // }
        if (givenColor) {
            color = givenColor
        }

        let styles = {
            'width': progress + '%',
            'background-color': color
        };

        return styles
    }

    getCenterCoordinates(): { latitude: number, longitude: number } {
        switch (this.customerId) {
            case 1:
                return {
                    latitude: 60.18208069645202,
                    longitude: 23.97548906518469
                };
            case 2:
                return {
                    latitude: 60.03611032213076,
                    longitude: 18.54047482522265
                };
            case 3: {
                // Prefer powerlines if present; fallback to type3; then frames
                const dynamicCenter = this.getCenterFromWorkOrders(this.powerLineWorkOrders)

                return dynamicCenter ?? { latitude: 60.656023, longitude: 25.279696 };
            }
            case 4:
                return {
                    latitude: 62.1250521303749,
                    longitude: 27.7926603631823
                }
            default:
                return {
                    latitude: 0, // Default latitude
                    longitude: 0 // Default longitude
                };
        }
    }

    private getCenterFromWorkOrders(workOrders: WorkOrder[]): { latitude: number; longitude: number } | null {
        if (!Array.isArray(workOrders) || workOrders.length === 0) return null;

        const points = workOrders
            .map(wo => ({
                lat: Number((wo as any).latitude),
                lng: Number((wo as any).longitude),
            }))
            .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));

        if (points.length === 0) return null;

        // center of bounding box (simple + stable)
        let minLat = points[0].lat, maxLat = points[0].lat;
        let minLng = points[0].lng, maxLng = points[0].lng;

        for (const p of points) {
            if (p.lat < minLat) minLat = p.lat;
            if (p.lat > maxLat) maxLat = p.lat;
            if (p.lng < minLng) minLng = p.lng;
            if (p.lng > maxLng) maxLng = p.lng;
        }

        return {
            latitude: (minLat + maxLat) / 2,
            longitude: (minLng + maxLng) / 2,
        };
    }

    getAmount(state) {
        if (state.type === 3) {
            let amount = 0
            if (this.allStates) {
                this.allStates.states.forEach(element => {
                    if (element.type === 3) {
                        amount += element.workorders.amount
                    }
                });
            }
            return amount
        } else if (state.type === 2) {
            let amount = 0
            if (this.allStates) {
                this.allStates.states.forEach(element => {
                    if (element.type === 2) {
                        amount += element.workorders.amount
                    }
                });
            }
            return amount
        } else if (state.type === 1) {
            let amount = 0
            if (this.allStates) {
                this.allStates.states.forEach(element => {
                    if (element.type === 1) {
                        amount += element.workorders.amount
                    }
                });
            }
            return amount
        } else return state.workorders.totaltotal
    }

    getDoneAmount(state) {
        let amount = 0
        let states = this.allStates.states.filter(s => s.type === 3)
        states.forEach(element => {
            if (element.state !== 1 && element.state >= state.state) {
                amount += element.workorders.amount
            }
        });
        return amount
    }

    private clearMarkerCollection(col: Map<number, L.Marker>) {
        col.forEach(m => m.remove());
        col.clear();
    }

    applyLayerFilter(): void {
        if (!this.map) return;

        switch (this.layerFilter) {
            case MapLayerFilter.ALL:
                this.drawWorkOrders();
                this.drawInverters();
                this.drawType3Workorders();
                this.drawPowerLineWorkOrders(); // keep powerlines in ALL
                break;

            case MapLayerFilter.FRAMES:
                this.drawWorkOrders(); // type 1
                this.clearMarkerCollection(this.inverterMarkers);
                this.clearMarkerCollection(this.type3WorkOrderMarkers);
                this.clearMarkerCollection(this.powerLineWorkOrderMarkers);
                break;

            case MapLayerFilter.INVERTERS:
                this.drawInverters(); // type 2
                this.clearMarkerCollection(this.workOrderMarkers);
                this.clearMarkerCollection(this.type3WorkOrderMarkers);
                this.clearMarkerCollection(this.powerLineWorkOrderMarkers);
                break;
        }
    }

    onLayerChange(value: MapLayerFilter | string): void {
        this.layerFilter = value as MapLayerFilter;
        this.applyLayerFilter();
    }

    // Route planning methods
    private drawBases(projectId: number): void {
        this.routePlanningService.getBases(projectId)
            .subscribe(bases => {
                bases.forEach(base => {
                    const marker = MapUtils.createBaseIcon(base, this.baseMarkers, this.map, this.translateService, true, false);
                    marker.on('click', () => this.showBaseDetails(base));
                });
            });
    }

    private drawRoutes(projectId: number): void {
        this.routePlanningService.getRoutes(projectId)
            .subscribe(routes => {
                routes.forEach(route => {
                    const polylineLayer = MapUtils.createRoutePolyline(this.map, route);
                    if (!polylineLayer) return;

                    const title = route.identifier
                        ? route.identifier
                        : this.translateService.instant('planner.routePlanning.route') + ' #' + route.id;

                    MapUtils.setPolylineTooltip(this.map, this.routeTooltip, polylineLayer, `
                        <div class="title">${title}</div>
                        ${route.notes ? '<div class="notes">' + route.notes + '</div>' : ''}
                    `);

                    polylineLayer.on('click', () => this.showRouteDetails(route));
                });
            });
    }

    private drawRouteNotes(projectId: number): void {
        this.routeNoteMarkers.forEach(marker => marker.remove());
        this.routeNoteMarkers.clear();

        this.routePlanningService.getRouteNotes(projectId)
            .subscribe(routeNotes => {
                routeNotes.forEach(routeNote => {
                    const marker = MapUtils.createRouteNoteIcon(routeNote);
                    this.routeNoteMarkers.set(routeNote.id, marker);

                    marker.bindTooltip(routeNote.notes, {
                        direction: 'top',
                        offset: [0, -10],
                        className: 'route-note-tooltip'
                    });

                    marker.addTo(this.map);

                    marker.on('click', () => this.showRouteNoteDetails(routeNote));
                });
            });
    }

    private showBaseDetails(base: RoutePlanningBase): void {
        this.routePlanningService.getReportsForBase(base.id).subscribe({
            next: reports => {
                this.selectedBase = base;
                this.selectedBaseReports = reports.sort((a, b) => {
                    if (!a.attend_time || !b.attend_time) return 0;
                    return new Date(b.attend_time).getTime() - new Date(a.attend_time).getTime();
                });

                this.showBaseSidebar = true;
                this.showSidebar = false;
                this.showRouteSidebar = false;
                this.showRouteNoteSidebar = false;

                for (let report of this.selectedBaseReports) {
                    if (!report?.images) continue;
                    for (let image of report.images) {
                        if (!image.id) continue;
                        this.workOrderService.fetchImage(image.id).subscribe(imageUrl => {
                            image.url = imageUrl;
                        });
                    }
                }
            },
            error: error => {
                this.toastService.sendToast(false, this.translateService.instant('planner.projectDashboard.reportsNotFound'));
            }
        });
    }

    private showRouteDetails(route: RoutePlanningRoute): void {
        this.selectedRoute = route;
        this.showRouteSidebar = true;
        this.showBaseSidebar = false;
        this.showRouteNoteSidebar = false;
        this.showSidebar = false;
    }

    private showRouteNoteDetails(routeNote: RouteNote): void {
        this.selectedRouteNote = routeNote;
        this.showRouteNoteSidebar = true;
        this.showRouteSidebar = false;
        this.showBaseSidebar = false;
        this.showSidebar = false;
    }
    formatReportDateTime(dateString: string): string {
        return DateUtils.formatUtcToLocal(dateString, 'datetime');
    }
}


